import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Gift,
  MapPin,
  PackageCheck,
  Phone,
  ShieldCheck,
  ShoppingBag,
  Tag,
  Truck,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { apiRequest } from "../services/api";

const CART_KEY = "cart";
const PENDING_ONLINE_ORDER_KEY_PREFIX = "pendingOnlineOrderId";
const FREE_DELIVERY_THRESHOLD = 4999;
const STANDARD_DELIVERY_FEE = 99;

const CASHFREE_MODE =
  process.env.REACT_APP_CASHFREE_ENV === "production"
    ? "production"
    : "sandbox";

let cashfreeSdkPromise;

const getCashfree = () => {
  if (!cashfreeSdkPromise) {
    cashfreeSdkPromise = load({
      mode: CASHFREE_MODE,
    });
  }

  return cashfreeSdkPromise;
};

const steps = ["Contact", "Address", "Delivery", "Payment"];

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const readCart = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(CART_KEY)) || [];

    if (!Array.isArray(stored)) return [];

    return stored.map((item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity ?? item.qty ?? 1)),
      price: Number(item.price || 0),
      mrp: Number(item.mrp ?? item.price ?? 0),
    }));
  } catch {
    return [];
  }
};

const getImageUrl = (image) => {
  if (!image || typeof image !== "string") return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `${process.env.REACT_APP_ASSET_URL || ""}${image}`;
};

const getErrorMessage = (error) => {
  if (Array.isArray(error?.details?.message)) {
    return error.details.message.join(", ");
  }

  return error?.message || "Something went wrong. Please try again.";
};

export default function CheckoutPage() {
  const navigate = useNavigate();

  const {
    customer,
    accessToken,
    isAuthenticated,
    isLoading: isCustomerLoading,
  } = useCustomerAuth();

  // A pending online order belongs to one customer only. A single global key
  // would let a later customer session reuse another customer's order ID.
  const pendingOnlineOrderKey = useMemo(() => {
    if (!customer?.id) {
      return null;
    }

    return `${PENDING_ONLINE_ORDER_KEY_PREFIX}:${customer.id}`;
  }, [customer?.id]);

  const [cart, setCart] = useState(readCart);
  const [step, setStep] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState("delivery");
  const [paymentMethod] = useState("online");

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const [couponInput, setCouponInput] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");

  const [loyaltyPointsInput, setLoyaltyPointsInput] = useState("");
  const [appliedLoyaltyPoints, setAppliedLoyaltyPoints] = useState(0);

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [isQuoting, setIsQuoting] = useState(false);

  const customerName =
    customer?.fullName ||
    customer?.name ||
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
    "";

  const [form, setForm] = useState({
    name: customerName,
    email: customer?.email || "",
    phone: customer?.phone || "",
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      name: current.name || customerName,
      email: current.email || customer?.email || "",
      phone: current.phone || customer?.phone || "",
    }));
  }, [customerName, customer?.email, customer?.phone]);

  useEffect(() => {
    if (!isCustomerLoading && !isAuthenticated) {
      navigate("/signin", {
        replace: true,
        state: { from: "/checkout" },
      });
    }
  }, [isCustomerLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!cart.length && !placedOrder) {
      navigate("/cart");
    }
  }, [cart.length, navigate, placedOrder]);

  useEffect(() => {
    const loadAddresses = async () => {
      if (!accessToken) return;

      try {
        setIsLoadingAddresses(true);

        const response = await apiRequest("/customer-account/addresses", {
          accessToken,
        });

        const savedAddresses = response || [];

        setAddresses(savedAddresses);

        const defaultAddress =
          savedAddresses.find((address) => address.isDefault) ||
          savedAddresses[0];

        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        }
      } catch (requestError) {
        setError(getErrorMessage(requestError));
      } finally {
        setIsLoadingAddresses(false);
      }
    };

    if (isAuthenticated) {
      loadAddresses();
    }
  }, [accessToken, isAuthenticated]);

  useEffect(() => {
    const loadInitialQuote = async () => {
      if (!accessToken || !isAuthenticated || !cart.length) return;

      try {
        await getQuote();
      } catch {
        // Local totals remain visible if the quote cannot load.
      }
    };

    loadInitialQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAuthenticated, cart]);

  const selectedAddress = addresses.find(
    (address) => address.id === selectedAddressId,
  );

  const summary = useMemo(() => {
    const mrpTotal = cart.reduce(
      (total, item) => total + item.mrp * item.quantity,
      0,
    );

    const localProductTotal = cart.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );

    const productTotal = Number(quote?.subtotalAmount ?? localProductTotal);
    const productDiscount = Math.max(0, mrpTotal - productTotal);
    const couponDiscount = Number(quote?.couponDiscountAmount || 0);
    const loyaltyDiscount = Number(quote?.loyaltyDiscountAmount || 0);

    const deliveryFee =
      quote?.shippingAmount !== undefined
        ? Number(quote.shippingAmount)
        : productTotal === 0 || productTotal >= FREE_DELIVERY_THRESHOLD
          ? 0
          : STANDARD_DELIVERY_FEE;

    return {
      itemCount: cart.reduce((total, item) => total + item.quantity, 0),
      mrpTotal,
      productTotal,
      productDiscount,
      couponDiscount,
      loyaltyDiscount,
      deliveryFee,
      total:
        quote?.totalAmount !== undefined
          ? Number(quote.totalAmount)
          : productTotal + deliveryFee,
    };
  }, [cart, quote]);

  const syncCartFromQuote = useCallback((response) => {
    if (!Array.isArray(response?.items)) return;

    const quotedItemsByVariantId = new Map(
      response.items.map((item) => [item.variantId, item]),
    );

    setCart((currentCart) => {
      let changed = false;

      const nextCart = currentCart.map((cartItem) => {
        const quotedItem = quotedItemsByVariantId.get(cartItem.variantId);

        if (!quotedItem) return cartItem;

        const unitPrice = Number(quotedItem.unitPrice || 0);
        const mrp = Number(quotedItem.mrp ?? unitPrice);
        const availableQuantity = Number(quotedItem.availableQuantity || 0);
        const quantity = Math.min(
          Number(cartItem.quantity || 1),
          Math.max(1, Math.floor(availableQuantity || 1)),
        );

        if (
          Number(cartItem.price) === unitPrice &&
          Number(cartItem.mrp) === mrp &&
          Number(cartItem.availableQuantity) === availableQuantity &&
          Number(cartItem.quantity) === quantity
        ) {
          return cartItem;
        }

        changed = true;

        return {
          ...cartItem,
          price: unitPrice,
          mrp,
          availableQuantity,
          quantity,
        };
      });

      if (!changed) return currentCart;

      localStorage.setItem(CART_KEY, JSON.stringify(nextCart));
      window.dispatchEvent(new Event("cartUpdated"));
      return nextCart;
    });
  }, []);

  const getQuote = async (
    couponCode = appliedCouponCode,
    loyaltyPoints = appliedLoyaltyPoints,
  ) => {
    const invalidItem = cart.find((item) => !item.variantId);

    if (invalidItem) {
      throw new Error(
        "One or more cart products are from an old cart. Remove and add them again before checkout.",
      );
    }

    setIsQuoting(true);
    setQuoteError("");

    try {
      const response = await apiRequest("/customer-orders/quote", {
        method: "POST",
        accessToken,
        body: {
          ...(couponCode ? { couponCode } : {}),
          ...(loyaltyPoints > 0
            ? { loyaltyPointsToRedeem: Number(loyaltyPoints) }
            : {}),
          items: cart.map((item) => ({
            variantId: item.variantId,
            quantity: Number(item.quantity),
          })),
        },
      });

      syncCartFromQuote(response);
      setQuote(response);
      return response;
    } catch (requestError) {
      const message = getErrorMessage(requestError);
      setQuoteError(message);
      throw new Error(message);
    } finally {
      setIsQuoting(false);
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();

    if (!code) {
      setAppliedCouponCode("");
      setCouponInput("");
      await getQuote("", appliedLoyaltyPoints);
      return;
    }

    try {
      await getQuote(code, appliedLoyaltyPoints);
      setAppliedCouponCode(code);
      setCouponInput(code);
    } catch {
      // Error is shown in the summary.
    }
  };

  const removeCoupon = async () => {
    setAppliedCouponCode("");
    setCouponInput("");

    try {
      await getQuote("", appliedLoyaltyPoints);
    } catch {
      // Error is already shown.
    }
  };

  const applyLoyaltyPoints = async () => {
    const points = Number(loyaltyPointsInput || 0);

    if (!Number.isInteger(points) || points < 0) {
      setQuoteError("Enter a valid whole number of loyalty points.");
      return;
    }

    try {
      await getQuote(appliedCouponCode, points);
      setAppliedLoyaltyPoints(points);
      setLoyaltyPointsInput(points ? String(points) : "");
    } catch {
      // Error is already shown.
    }
  };

  const removeLoyaltyPoints = async () => {
    setAppliedLoyaltyPoints(0);
    setLoyaltyPointsInput("");

    try {
      await getQuote(appliedCouponCode, 0);
    } catch {
      // Error is already shown.
    }
  };

  const updateForm = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));
  };

  const validateCurrentStep = () => {
    const nextErrors = {};

    if (step === 0) {
      if (!form.name.trim()) nextErrors.name = "Please enter your full name.";

      if (!form.email.trim()) {
        nextErrors.email = "Please enter your email address.";
      } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        nextErrors.email = "Enter a valid email address.";
      }

      if (!form.phone.trim()) {
        nextErrors.phone = "Please enter your phone number.";
      } else if (form.phone.replace(/\D/g, "").length < 10) {
        nextErrors.phone = "Enter a valid 10-digit phone number.";
      }
    }

    if (step === 1 && !selectedAddressId) {
      nextErrors.address = "Please select a delivery address.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    setError("");

    if (!validateCurrentStep()) return;

    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const goBack = () => {
    setErrors({});
    setError("");
    setStep((current) => Math.max(current - 1, 0));
  };

  const getReusablePendingOnlineOrder = async () => {
    if (!pendingOnlineOrderKey) {
      return null;
    }

    const pendingOrderId = localStorage.getItem(pendingOnlineOrderKey);

    if (!pendingOrderId) {
      return null;
    }

    try {
      const response = await apiRequest(
        `/customer-orders/${encodeURIComponent(pendingOrderId)}`,
        { accessToken },
      );

      const pendingOrder = response?.order || response;

      const canResumePayment =
        pendingOrder?.id === pendingOrderId &&
        pendingOrder?.paymentMethod === "ONLINE" &&
        pendingOrder?.paymentStatus === "PENDING" &&
        pendingOrder?.status === "PENDING_PAYMENT";

      if (canResumePayment) {
        return pendingOrder;
      }
    } catch {
      // The order can have been cancelled, deleted, or belong to a different
      // customer after a previous browser session. Start a new checkout below.
    }

    localStorage.removeItem(pendingOnlineOrderKey);
    return null;
  };

  const placeOrder = async () => {
    setError("");

    if (!validateCurrentStep()) return;

    const invalidItem = cart.find((item) => !item.variantId);

    if (invalidItem) {
      setError(
        "One or more cart products are from an old cart. Remove and add them again before checkout.",
      );
      return;
    }

    try {
      await getQuote();
    } catch {
      return;
    }

    setIsPlacingOrder(true);

    try {
      const isOnlinePayment = paymentMethod === "online";
      let order = isOnlinePayment
        ? await getReusablePendingOnlineOrder()
        : null;

      if (!order) {
        order = await apiRequest("/customer-orders", {
          method: "POST",
          accessToken,
          body: {
            addressId: selectedAddressId,
            paymentMethod: "ONLINE",
            ...(appliedCouponCode ? { couponCode: appliedCouponCode } : {}),
            ...(appliedLoyaltyPoints > 0
              ? { loyaltyPointsToRedeem: appliedLoyaltyPoints }
              : {}),
            items: cart.map((item) => ({
              variantId: item.variantId,
              quantity: Number(item.quantity),
            })),
          },
        });

        if (
          isOnlinePayment &&
          order.paymentStatus !== "PAID" &&
          pendingOnlineOrderKey
        ) {
          localStorage.setItem(pendingOnlineOrderKey, order.id);
        }
      }

      if (isOnlinePayment && order.paymentStatus === "PAID") {
        localStorage.removeItem(CART_KEY);

        if (pendingOnlineOrderKey) {
          localStorage.removeItem(pendingOnlineOrderKey);
        }
        window.dispatchEvent(new Event("cartUpdated"));

        setCart([]);
        setPlacedOrder(order);
        return;
      }

      if (isOnlinePayment) {
        const session = await apiRequest(
          `/customer-orders/${order.id}/payments/cashfree/session`,
          {
            method: "POST",
            accessToken,
          },
        );

        if (!session?.paymentSessionId) {
          throw new Error("Cashfree did not return a payment session.");
        }

        const cashfree = await getCashfree();

        if (!cashfree) {
          throw new Error(
            "Cashfree checkout could not be loaded. Please check your connection and try again.",
          );
        }

        await cashfree.checkout({
          paymentSessionId: session.paymentSessionId,
          redirectTarget: "_self",
        });

        return;
      }

      localStorage.removeItem(CART_KEY);
      window.dispatchEvent(new Event("cartUpdated"));

      setCart([]);
      setPlacedOrder(order);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (isCustomerLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  if (placedOrder) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7] px-4 py-12">
        <section className="w-full max-w-2xl border border-[#ded8d4] bg-white p-7 text-center sm:p-12">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 size={32} />
          </div>

          <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-primary-800">
            Order confirmed
          </p>

          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Thank you for shopping with CHARMS.
          </h1>

          <p className="mt-4 text-sm leading-6 text-slate-500">
            Your order reference is{" "}
            <span className="font-semibold text-slate-950">
              {placedOrder.orderNumber}
            </span>
            .
          </p>

          <div className="mt-8 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
            <div className="bg-white px-5 py-5 text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Payment method
              </p>
              <p className="mt-2 font-semibold text-slate-950">
                {Number(placedOrder.totalAmount) <= 0
                  ? "Paid with rewards / offers"
                  : "Online payment"}
              </p>
            </div>

            <div className="bg-white px-5 py-5 text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Order total
              </p>
              <p className="mt-2 font-semibold text-slate-950">
                {formatCurrency(placedOrder.totalAmount)}
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => navigate("/userprofile")}
              className="border border-slate-900 py-4 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-white"
            >
              View my orders
            </button>

            <button
              type="button"
              onClick={() => navigate("/search/all")}
              className="bg-primary-800 py-4 text-sm font-bold text-white transition hover:bg-primary-950"
            >
              Continue shopping
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#faf9f7]">
      {/* Checkout heading */}
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-9 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate("/cart")}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 transition hover:text-primary-800"
          >
            <ChevronLeft size={15} />
            Back to bag
          </button>

          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
                Secure checkout
              </p>

              <h1 className="mt-2 text-4xl font-semibold tracking-[-0.045em] text-slate-950 sm:text-5xl">
                Complete your order
              </h1>
            </div>

            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <ShieldCheck size={16} className="text-primary-800" />
              Your information is securely handled.
            </div>
          </div>

          {/* Steps */}
          <div className="mt-9 grid grid-cols-4 gap-2 sm:gap-4">
            {steps.map((label, index) => (
              <div key={label}>
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                      index <= step
                        ? "bg-primary-800 text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {index + 1}
                  </span>

                  <span
                    className={`hidden text-xs font-bold uppercase tracking-[0.11em] sm:block ${
                      index <= step ? "text-slate-950" : "text-slate-400"
                    }`}
                  >
                    {label}
                  </span>
                </div>

                <div
                  className={`mt-3 h-px ${
                    index < step ? "bg-primary-800" : "bg-slate-200"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-7 border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_370px]">
          {/* Checkout steps */}
          <section>
            {step === 0 ? (
              <CheckoutPanel
                icon={Phone}
                eyebrow="Step 1"
                title="Contact details"
                description="We will use these details to send you order updates."
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Full name"
                    name="name"
                    value={form.name}
                    onChange={updateForm}
                    error={errors.name}
                    placeholder="Your full name"
                  />

                  <Field
                    label="Phone number"
                    name="phone"
                    value={form.phone}
                    onChange={updateForm}
                    error={errors.phone}
                    placeholder="98765 43210"
                    inputMode="numeric"
                  />

                  <div className="sm:col-span-2">
                    <Field
                      label="Email address"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={updateForm}
                      error={errors.email}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <p className="mt-6 text-xs leading-5 text-slate-400">
                  You can permanently change saved account details from your
                  customer profile.
                </p>
              </CheckoutPanel>
            ) : null}

            {step === 1 ? (
              <CheckoutPanel
                icon={MapPin}
                eyebrow="Step 2"
                title="Delivery address"
                description="Select where you would like your order delivered."
              >
                {isLoadingAddresses ? (
                  <div className="h-36 animate-pulse bg-slate-100" />
                ) : addresses.length === 0 ? (
                  <div className="border border-primary-100 bg-primary-50 p-6">
                    <h3 className="font-semibold text-primary-950">
                      Add a delivery address first
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-primary-800">
                      Your order needs a saved address before it can be placed.
                    </p>

                    <button
                      type="button"
                      onClick={() => navigate("/userprofile")}
                      className="mt-5 bg-primary-800 px-4 py-3 text-sm font-bold text-white transition hover:bg-primary-950"
                    >
                      Manage addresses
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {addresses.map((address) => {
                      const isSelected = selectedAddressId === address.id;

                      return (
                        <label
                          key={address.id}
                          className={`block cursor-pointer border p-5 transition ${
                            isSelected
                              ? "border-primary-800 bg-primary-50"
                              : "border-slate-200 bg-white hover:border-primary-300"
                          }`}
                        >
                          <div className="flex gap-4">
                            <input
                              type="radio"
                              name="deliveryAddress"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedAddressId(address.id);
                                setErrors((current) => ({
                                  ...current,
                                  address: "",
                                }));
                              }}
                              className="mt-1 accent-primary-800"
                            />

                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-semibold text-slate-950">
                                  {address.label || "Address"}
                                </h3>

                                {address.isDefault ? (
                                  <span className="bg-primary-800 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-white">
                                    Default
                                  </span>
                                ) : null}
                              </div>

                              <p className="mt-3 text-sm font-medium text-slate-800">
                                {address.recipient} · {address.phone}
                              </p>

                              <p className="mt-1 text-sm leading-6 text-slate-500">
                                {address.addressLine1}
                                {address.addressLine2
                                  ? `, ${address.addressLine2}`
                                  : ""}
                                <br />
                                {address.city}, {address.state}{" "}
                                {address.postalCode}
                              </p>
                            </div>
                          </div>
                        </label>
                      );
                    })}

                    <button
                      type="button"
                      onClick={() => navigate("/userprofile")}
                      className="pt-2 text-xs font-bold uppercase tracking-[0.13em] text-primary-800"
                    >
                      + Add or edit addresses
                    </button>
                  </div>
                )}

                {errors.address ? (
                  <p className="mt-4 text-sm text-red-700">{errors.address}</p>
                ) : null}
              </CheckoutPanel>
            ) : null}

            {step === 2 ? (
              <CheckoutPanel
                icon={Truck}
                eyebrow="Step 3"
                title="Delivery method"
                description="Your order will be delivered to the address you selected."
              >
                <label className="flex cursor-pointer items-start justify-between gap-4 border border-primary-800 bg-primary-50 p-5">
                  <div className="flex gap-3">
                    <input
                      type="radio"
                      name="deliveryMethod"
                      checked={deliveryMethod === "delivery"}
                      onChange={() => setDeliveryMethod("delivery")}
                      className="mt-1 accent-primary-800"
                    />

                    <div>
                      <p className="font-semibold text-slate-950">
                        Standard delivery
                      </p>

                      <p className="mt-1 text-sm leading-6 text-slate-500">
                        {summary.productTotal >= FREE_DELIVERY_THRESHOLD
                          ? "Free delivery is available on this order."
                          : `${formatCurrency(
                              STANDARD_DELIVERY_FEE,
                            )} delivery fee. Free delivery above ${formatCurrency(
                              FREE_DELIVERY_THRESHOLD,
                            )}.`}
                      </p>
                    </div>
                  </div>

                  <span className="text-sm font-bold text-primary-800">
                    {summary.productTotal >= FREE_DELIVERY_THRESHOLD
                      ? "Free"
                      : formatCurrency(STANDARD_DELIVERY_FEE)}
                  </span>
                </label>
              </CheckoutPanel>
            ) : null}

            {step === 3 ? (
              <CheckoutPanel
                icon={CreditCard}
                eyebrow="Step 4"
                title="Payment method"
                description="Choose how you would like to pay for your order."
              >
                <div className="grid gap-3">
                  <label
                    className={`flex cursor-pointer items-start justify-between gap-4 border p-5 transition ${
                      paymentMethod === "online"
                        ? "border-primary-800 bg-primary-50"
                        : "border-slate-200 bg-white hover:border-primary-300"
                    }`}
                  >
                    <div className="flex gap-3">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="online"
                        checked={paymentMethod === "online"}
                        readOnly
                        className="mt-1 accent-primary-800"
                      />

                      <div>
                        <p className="font-semibold text-slate-950">
                          Pay online
                        </p>

                        <p className="mt-1 text-sm leading-6 text-slate-500">
                          Pay securely by UPI, card, net banking, or wallet.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                {paymentMethod === "online" ? (
                  <p className="mt-5 border-l-2 border-primary-800 bg-[#faf7f5] px-4 py-3 text-sm leading-6 text-slate-500">
                    You will be redirected to our payment gateway after placing
                    the order. Your order is confirmed only after the payment is
                    verified.
                  </p>
                ) : null}
              </CheckoutPanel>
            ) : null}

            {/* Step navigation */}
            <div className="mt-7 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={goBack}
                disabled={step === 0}
                className="inline-flex items-center gap-1 border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-slate-900 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-35"
              >
                <ChevronLeft size={17} />
                Back
              </button>

              {step < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center gap-2 bg-primary-800 px-6 py-3 text-sm font-bold text-white transition hover:bg-primary-950"
                >
                  Continue
                  <ChevronRight size={17} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={placeOrder}
                  disabled={isPlacingOrder || !selectedAddress || !cart.length}
                  className="inline-flex items-center gap-2 bg-primary-800 px-6 py-3 text-sm font-bold text-white transition hover:bg-primary-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isPlacingOrder
                    ? "Preparing payment..."
                    : "Proceed to secure payment"}
                  <PackageCheck size={17} />
                </button>
              )}
            </div>
          </section>

          {/* Order summary */}
          <aside className="h-fit border border-[#ded8d4] bg-white p-6 lg:sticky lg:top-32">
            <div className="flex items-center gap-3 border-b border-slate-200 pb-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-800">
                <ShoppingBag size={18} />
              </div>

              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Order summary
                </h2>

                <p className="mt-0.5 text-xs text-slate-500">
                  {summary.itemCount} item
                  {summary.itemCount > 1 ? "s" : ""} in your order
                </p>
              </div>
            </div>

            <div className="max-h-64 space-y-4 overflow-y-auto border-b border-slate-200 py-5 pr-1">
              {cart.map((item, index) => {
                const imageUrl = getImageUrl(item.image);

                return (
                  <div
                    key={item.variantId || `${item.id}-${index}`}
                    className="flex gap-3"
                  >
                    <div className="h-16 w-12 shrink-0 overflow-hidden bg-slate-100">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-primary-100 text-xs font-bold text-primary-800">
                          C
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-semibold text-slate-950">
                        {item.title}
                      </p>
                      {item.productCode && <p className="mt-1 text-xs text-slate-500">Article No: {item.productCode}</p>}

                      <p className="mt-1 text-xs text-slate-500">
                        {item.quantity} × {formatCurrency(item.price)}
                      </p>

                      {item.size || item.color ? (
                        <p className="mt-1 text-[11px] text-slate-400">
                          {[item.color, item.size].filter(Boolean).join(" · ")}
                        </p>
                      ) : null}
                    </div>

                    <span className="text-xs font-bold text-slate-950">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                );
              })}
            </div>

            {quoteError ? (
              <div className="mt-5 border border-red-100 bg-red-50 px-3 py-3 text-xs leading-5 text-red-700">
                {quoteError}
              </div>
            ) : null}

            {/* Coupon */}
            <div className="border-b border-slate-200 py-5">
              <div className="mb-3 flex items-center gap-2">
                <Tag size={16} className="text-primary-800" />
                <h3 className="text-sm font-semibold text-slate-950">
                  Coupon code
                </h3>
              </div>

              {appliedCouponCode ? (
                <div className="flex items-center justify-between border border-primary-100 bg-primary-50 px-3 py-3">
                  <div>
                    <p className="font-mono text-sm font-bold text-primary-800">
                      {appliedCouponCode}
                    </p>
                    <p className="mt-1 text-xs text-emerald-700">
                      {formatCurrency(summary.couponDiscount)} discount applied
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="flex h-8 w-8 items-center justify-center text-primary-800 transition hover:bg-primary-100"
                    aria-label="Remove coupon"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(event) =>
                      setCouponInput(event.target.value.toUpperCase())
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        applyCoupon();
                      }
                    }}
                    placeholder="Enter code"
                    className="h-11 min-w-0 flex-1 border border-slate-300 px-3 text-sm uppercase outline-none transition focus:border-primary-800"
                  />

                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={isQuoting}
                    className="bg-slate-950 px-4 text-xs font-bold uppercase tracking-[0.1em] text-white transition hover:bg-primary-800 disabled:opacity-60"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Loyalty */}
            <div className="border-b border-slate-200 py-5">
              <div className="mb-2 flex items-center gap-2">
                <Gift size={16} className="text-primary-800" />
                <h3 className="text-sm font-semibold text-slate-950">
                  Redeem rewards
                </h3>
              </div>

              <p className="mb-3 text-xs leading-5 text-slate-500">
                Available:{" "}
                <span className="font-bold text-primary-800">
                  {quote?.loyalty?.availablePoints ?? 0} points
                </span>
                {" · "}1 point = ₹1
              </p>

              {appliedLoyaltyPoints > 0 ? (
                <div className="flex items-center justify-between border border-primary-100 bg-primary-50 px-3 py-3">
                  <div>
                    <p className="text-sm font-semibold text-primary-800">
                      {appliedLoyaltyPoints} points redeemed
                    </p>
                    <p className="mt-1 text-xs text-emerald-700">
                      {formatCurrency(summary.loyaltyDiscount)} discount applied
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={removeLoyaltyPoints}
                    className="flex h-8 w-8 items-center justify-center text-primary-800 transition hover:bg-primary-100"
                    aria-label="Remove loyalty redemption"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={loyaltyPointsInput}
                    onChange={(event) =>
                      setLoyaltyPointsInput(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        applyLoyaltyPoints();
                      }
                    }}
                    placeholder="Points"
                    className="h-11 min-w-0 flex-1 border border-slate-300 px-3 text-sm outline-none transition focus:border-primary-800"
                  />

                  <button
                    type="button"
                    onClick={applyLoyaltyPoints}
                    disabled={isQuoting}
                    className="bg-slate-950 px-4 text-xs font-bold uppercase tracking-[0.1em] text-white transition hover:bg-primary-800 disabled:opacity-60"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="space-y-3 border-b border-slate-200 py-5 text-sm">
              <SummaryRow
                label="Total MRP"
                value={formatCurrency(summary.mrpTotal)}
              />

              {summary.productDiscount > 0 ? (
                <SummaryRow
                  label="Product discount"
                  value={`-${formatCurrency(summary.productDiscount)}`}
                  valueClass="text-emerald-700"
                />
              ) : null}

              {summary.couponDiscount > 0 ? (
                <SummaryRow
                  label="Coupon discount"
                  value={`-${formatCurrency(summary.couponDiscount)}`}
                  valueClass="text-emerald-700"
                />
              ) : null}

              {summary.loyaltyDiscount > 0 ? (
                <SummaryRow
                  label="Rewards redeemed"
                  value={`-${formatCurrency(summary.loyaltyDiscount)}`}
                  valueClass="text-emerald-700"
                />
              ) : null}

              <SummaryRow
                label="Delivery"
                value={
                  summary.deliveryFee
                    ? formatCurrency(summary.deliveryFee)
                    : "Free"
                }
                valueClass={summary.deliveryFee ? "" : "text-emerald-700"}
              />
            </div>

            <div className="flex items-end justify-between pt-5">
              <span className="text-base font-semibold text-slate-950">
                Total amount
              </span>

              <span className="text-2xl font-semibold tracking-tight text-slate-950">
                {formatCurrency(summary.total)}
              </span>
            </div>

            <div className="mt-5 flex items-start gap-2 text-xs leading-5 text-slate-500">
              <ShieldCheck
                size={16}
                className="mt-0.5 shrink-0 text-primary-800"
              />

              <span>
                {isQuoting
                  ? "Checking live stock, offers and rewards..."
                  : "Final stock, offer and loyalty checks happen securely when you place the order."}
              </span>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function CheckoutPanel({ icon: Icon, eyebrow, title, description, children }) {
  return (
    <section className="border border-[#ded8d4] bg-white p-6 sm:p-8">
      <div className="mb-7 flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-800">
          <Icon size={18} />
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-primary-800">
            {eyebrow}
          </p>

          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
            {title}
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
        </div>
      </div>

      {children}
    </section>
  );
}

function Field({ label, error, disabled = false, type = "text", ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-slate-600">
        {label}
      </span>

      <input
        {...props}
        type={type}
        disabled={disabled}
        className={`h-12 w-full border px-4 text-sm outline-none transition ${
          error
            ? "border-red-400 focus:ring-2 focus:ring-red-100"
            : "border-slate-300 focus:border-primary-800 focus:ring-2 focus:ring-primary-100"
        } ${disabled ? "bg-slate-50 text-slate-500" : "bg-white text-slate-950"}`}
      />

      {error ? <p className="mt-1.5 text-xs text-red-700">{error}</p> : null}
    </label>
  );
}

function SummaryRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium text-slate-950 ${valueClass}`}>
        {value}
      </span>
    </div>
  );
}
